import React, { useCallback, useState } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { Button } from '../../../../../../widgets/buttons';
import { useDialogManager } from '../../../../../../widgets/dialog';
import { showSuccessNotification } from '../../../../../../widgets/notificationToaster';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import styled from 'styled-components';

// LE DUE TEXTURE D'AMBIENTE, IN UN POSTO SOLO. La mappa d'ambiente e lo skybox si caricano
// con la stessa procedura: controllo del piano, scelta dei file, validazione — un `.hdr`,
// `.dds`, `.env` oppure sei facce di cubemap con i suffissi giusti — e poi `loadTextureFiles`.
//
// Stava tutta dentro `settingsEditor`, ed è il motivo per cui il pannello World poteva
// mostrare la mappa ma non sostituirla: rifare qui la stessa procedura sarebbe stata la stessa
// procedura in due posti, e le due copie sarebbero divergute alla prima correzione.
//
// Ora la casa è World; le Impostazioni non le mostrano più.

const ErrorBody = styled.div`
    padding: 20px;
    text-align: center;
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;

    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }

    z-index: 10000 !important;
`;

/** Le facce di un cubemap, e l'ordine in cui il motore se le aspetta. */
const CUBEMAP_SUFFIXES = ['px', 'py', 'pz', 'nx', 'ny', 'nz'];

const SINGLE_FILE_EXTENSIONS = ['.hdr', '.dds', '.env', '.basis'];

const pickFiles = (onPicked: (files: File[]) => void) => {
    const input = document.createElement('input');
    input.setAttribute('multiple', 'multiple');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.jpeg,.jpg,.png,.hdr,.dds,.env');
    input.addEventListener('change', (event) => {
        const files = (event.currentTarget as HTMLInputElement).files;
        if (files && files.length > 0) onPicked(Array.from(files));
    });

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
};

export const useEnvironmentTextures = (sceneEditor: any, zkScene: any, additionalFeatures: any) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { sellerMarketplace, updateSubscription } = useSubscriptionData();

    const [environmentTexture, setEnvironmentTexture] = useState<any>(null);
    const [skyboxTexture, setSkyboxTexture] = useState<any>(null);
    const [isEnvironmentUploading, setIsEnvironmentUploading] = useState(false);
    const [isSkyboxUploading, setIsSkyboxUploading] = useState(false);
    const [skyboxBlur, setSkyboxBlurValue] = useState<number>((zkScene?.getOrCreateSkyboxConfig()?.get('blur') ?? 0) * 100);

    const showError = useCallback(
        (title: string, ...lines: string[]) => {
            showDialog(
                'environment-texture-error',
                <ErrorBody>
                    <h3>{title}</h3>
                    {lines.map((line) => (
                        <p key={line}>{line}</p>
                    ))}
                    <Button onClick={() => closeDialog('environment-texture-error')}>{T._('Close', 'Admin')}</Button>
                </ErrorBody>
            );
        },
        [showDialog, closeDialog]
    );

    /** Il piano copre le texture d'ambiente personalizzate? Se no, si propone l'upgrade. */
    const canCustomize = useCallback(
        (featureName: string) => {
            if (additionalFeatures?.customEnvironmentTexture) return true;

            showDialog(
                'Upgrade_Plan',
                <UpgradeBanner
                    marketPlace={sellerMarketplace ?? undefined}
                    message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), featureName)}
                    onUpgradeCompleted={async () => {
                        updateSubscription();
                    }}
                />
            );
            return false;
        },
        [additionalFeatures, sellerMarketplace, showDialog, updateSubscription]
    );

    /**
     * Un file solo va bene se è `.hdr`, `.dds`, `.env` o `.basis`; sei file vanno bene se
     * sono le sei facce, riconoscibili dal suffisso. Tutto il resto no — e il perché va detto,
     * perché «non funziona» su un'immagine singola sembra un difetto e non una regola.
     */
    const validateFiles = useCallback(
        (files: File[]): boolean => {
            const extension = '.' + (files[0].name.split('.').pop()?.toLowerCase() ?? '');

            if (files.length !== 6 && !SINGLE_FILE_EXTENSIONS.includes(extension)) {
                showError(
                    T._('Unsupported format', 'Admin'),
                    T._('Single image files are not supported as environment textures. Use .hdr, .dds, .env or upload 6 cubemap face images.', 'Admin')
                );
                return false;
            }

            if (files.length === 6) {
                const matched = new Set<string>();
                files.forEach((file) => {
                    const dotIndex = file.name.lastIndexOf('.');
                    if (dotIndex === -1) return;
                    const nameWithoutExtension = file.name.substring(0, dotIndex);
                    const suffix = CUBEMAP_SUFFIXES.find((candidate) => nameWithoutExtension.endsWith('_' + candidate));
                    if (suffix) matched.add(suffix);
                });

                const missing = CUBEMAP_SUFFIXES.filter((suffix) => !matched.has(suffix));
                if (missing.length > 0) {
                    showError(
                        T._('Invalid cubemap files', 'Admin'),
                        T._('Cubemap files must end with _px, _py, _pz, _nx, _ny, _nz before the extension (e.g. sky_px.jpg).', 'Admin'),
                        `Missing: ${missing.map((suffix) => '_' + suffix).join(', ')}`
                    );
                    return false;
                }
            }

            return true;
        },
        [showError]
    );

    const uploadEnvironment = useCallback(() => {
        if (!canCustomize('Grow')) return;

        pickFiles((files) => {
            if (!validateFiles(files)) return;

            setIsEnvironmentUploading(true);
            sceneEditor?.loadTextureFiles(
                files,
                null,
                'environment',
                false,
                () => {
                    setIsEnvironmentUploading(false);
                    // Il motore la monta subito dopo aver risposto: senza il rinvio si
                    // leggerebbe la scena un attimo prima che la texture ci sia.
                    setTimeout(() => setEnvironmentTexture(sceneEditor?.scene?.environmentTexture || true), 100);
                    showSuccessNotification({ message: T._('Environment texture updated successfully.', 'Admin') });
                },
                (reason: unknown) => {
                    setIsEnvironmentUploading(false);
                    showError(T._('Upload failed', 'Admin'), typeof reason === 'string' && reason ? reason : T._('Failed uploading texture.', 'Admin'));
                }
            );
        });
    }, [canCustomize, validateFiles, sceneEditor, showError]);

    const removeEnvironment = useCallback(() => {
        sceneEditor?.removeSceneEnvironmentTexture();
        setEnvironmentTexture(null);
    }, [sceneEditor]);

    const uploadSkybox = useCallback(() => {
        if (!canCustomize('Custom Environment Texture')) return;

        pickFiles((files) => {
            if (!validateFiles(files)) {
                sceneEditor?.setViewerSkyboxCreateSkybox(false);
                return;
            }

            setIsSkyboxUploading(true);
            // Lo skybox si vede solo nel modo viewer: accenderlo qui evita di caricare una
            // texture che poi sembra non essere arrivata.
            sceneEditor?.setEnvironmentMode(2);
            sceneEditor?.setViewerSkyboxCreateSkybox(true);

            sceneEditor?.loadTextureFiles(
                files,
                null,
                'skybox',
                false,
                () => {
                    setIsSkyboxUploading(false);
                    setTimeout(() => setSkyboxTexture(sceneEditor?.getSkyboxTexture?.() || true), 100);
                },
                () => {
                    setIsSkyboxUploading(false);
                    showError(T._('Upload failed', 'Admin'), T._('Failed uploading texture.', 'Admin'));
                }
            );
        });
    }, [canCustomize, validateFiles, sceneEditor, showError]);

    const removeSkybox = useCallback(() => {
        sceneEditor?.setViewerSkyboxCreateSkybox(false);
        sceneEditor?.removeViewerSkyboxTexture();
        setSkyboxTexture(null);
    }, [sceneEditor]);

    /** Il motore vuole 0–1, la riga mostra 0–100: la conversione sta qui e non nella schermata. */
    const setSkyboxBlur = useCallback(
        (value: number) => {
            setSkyboxBlurValue(value);
            sceneEditor?.setViewerSkyboxBlur(value / 100);
        },
        [sceneEditor]
    );

    return {
        environmentTexture,
        setEnvironmentTexture,
        skyboxTexture,
        setSkyboxTexture,
        isEnvironmentUploading,
        isSkyboxUploading,
        uploadEnvironment,
        removeEnvironment,
        uploadSkybox,
        removeSkybox,
        skyboxBlur,
        setSkyboxBlur
    };
};
